/**
 * What to do when the server answers 429 (too many requests).
 *
 * The server's limits are per signed-in user and generous (backend rate-limiter.middleware.js), so
 * a 429 should be rare: a burst that straddles the end of a one-minute window, or a runaway loop.
 * For the first, waiting the second or two the server asks for and trying once more is invisible
 * to the person and better than a screen that fails; for the second, retrying would only feed it.
 *
 * So a request is retried only when ALL of these hold:
 *  - it is a READ (GET/HEAD). A write is never replayed on the user's behalf: the server may have
 *    refused it for a reason, and "saved twice" is worse than "not saved, try again". Every
 *    credential attempt is a POST, so a refused sign-in always reaches the form as it is - while
 *    GET /auth/me, the "who am I" read every screen waits on, IS retried: losing it replaces the
 *    whole page with "we couldn't check your access".
 *  - the server says the wait is short (MAX_WAIT_SECONDS). "Come back in 40 seconds" is shown to
 *    the person, not slept through behind a spinner.
 *  - it has not been retried MAX_RETRIES times already.
 */
export const MAX_WAIT_SECONDS = 5
export const MAX_RETRIES = 2

interface RetryableRequest {
    method?: string
    url?: string
    _rateLimitRetries?: number
}

interface RateLimitedError {
    response?: { status?: number; headers?: Record<string, unknown>; data?: { retryAfter?: unknown } }
}

/** Seconds the server asked us to wait: the Retry-After header, else the body's retryAfter. */
export function retryAfterSeconds(error: RateLimitedError): number | null {
    const header = error.response?.headers?.['retry-after']
    const body = error.response?.data?.retryAfter
    for (const raw of [header, body]) {
        const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number.parseFloat(raw) : NaN
        if (Number.isFinite(n) && n >= 0) return n
    }
    return null
}

/**
 * Milliseconds to wait before trying `request` again, or null to let the 429 through.
 * `random` spreads the retries of many tabs so they do not all return in the same instant.
 */
export function rateLimitRetryDelay(error: RateLimitedError, request: RetryableRequest, random: () => number = Math.random): number | null {
    if (error.response?.status !== 429) return null
    const method = (request.method || 'get').toLowerCase()
    if (method !== 'get' && method !== 'head') return null
    if ((request._rateLimitRetries ?? 0) >= MAX_RETRIES) return null
    const seconds = retryAfterSeconds(error)
    if (seconds == null || seconds > MAX_WAIT_SECONDS) return null
    return Math.round(seconds * 1000 + random() * 400)
}
